import React, { useState, useEffect, FormEvent, useCallback } from 'react';
import axiosInstance from 'config/axiosInstance';
import Search from 'reusable/search';
import SortBy from 'reusable/sortby';
import Pagination from 'reusable/pagination';
import AttendanceTable from './AttendanceTable';
import EditDialog from 'reusable/Dialog/editDialog';
import {
  DialogConfig,
  DialogField,
  DialogType,
  NewDataField,
} from 'reusable/Dialog/dialogInterface';
import {
  attendanceSortingOptions,
  editAttendanceFields,
} from './AttendanceField';
import DateFilter from 'reusable/datafilter';
import { PAGE_SIZE } from 'config/constant';
import { useTranslation } from 'react-i18next';

const AttendanceManagement: React.FC = () => {
  const { t } = useTranslation(['attendance', 'common']);
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('');
  const [dateValue, setDateValue] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [overtimeValue, setOvertimeValue] = useState<boolean | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [attendanceData, setAttendanceData] = useState<any[]>([]);
  const [count, setCount] = useState(1);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState<DialogConfig>({
    title: '',
    subtitle: '',
    fields: [],
  });
  const [currentAttendanceId, setCurrentAttendanceId] = useState<string | null>(
    null
  );

  const fetchProfilePicture = async (userId: string) => {
    try {
      const response = await axiosInstance.get(`/v1/drivers/${userId}/`);
      return response.data.profile_picture;
    } catch (error) {
      console.error(
        `Failed to fetch profile picture for user ${userId}`,
        error
      );
      return null;
    }
  };

  const fetchAttendanceData = useCallback(async () => {
    const date = new Date(dateValue); // assuming dateValue is in 'YYYY-MM-DD' format

    // Set start date to the previous day at 15:00:00 UTC
    const startDate = new Date(date);
    startDate.setDate(startDate.getDate() - 1);
    startDate.setUTCHours(15, 0, 0, 0);
    const startOfDay = startDate.toISOString();

    // Set end date to the current day at 14:59:59 UTC
    const endDate = new Date(date);
    endDate.setUTCHours(14, 59, 59, 0);
    const endOfDay = endDate.toISOString();

    try {
      let apiUrl = `/v1/attendances/?check_in__gte=${startOfDay}&check_in__lte=${endOfDay}&page=${currentPage}&page_size=${PAGE_SIZE}`;
      if (sortValue) {
        apiUrl += `&ordering=${sortValue}`;
      }
      if (searchValue) {
        apiUrl += `&search=${encodeURIComponent(searchValue)}`;
      }
      if (overtimeValue !== null) {
        apiUrl += `&overtime_duration__isnull=${overtimeValue}`;
      }

      const response = await axiosInstance.get(apiUrl);
      const fetchedAttendanceData = response.data.results;

      const updatedAttendanceData = await Promise.all(
        fetchedAttendanceData.map(async (attendance: any) => {
          if (attendance.user && attendance.user.id) {
            const profilePicture = await fetchProfilePicture(attendance.user.id);
            return {
              ...attendance,
              user: { ...attendance.user, profile_picture: profilePicture },
            };
          }
          return attendance;
        })
      );

      setAttendanceData(updatedAttendanceData);
      setCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, [searchValue, dateValue, sortValue, overtimeValue, currentPage]);

  useEffect(() => {
    fetchAttendanceData();
  }, [fetchAttendanceData]);

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handleDateChange = (value: string) => {
    setDateValue(value);
    setCurrentPage(1);
  };

  const openDialog = (
    title: string,
    subtitle: string,
    fields: DialogField[],
    dialogType: DialogType
  ) => {
    setDialogProps({ title, subtitle, fields });
    if (dialogType === DialogType.Edit) setIsEditDialogOpen(true);
  };

  const closeDialog = () => {
    setIsEditDialogOpen(false);
    setCurrentAttendanceId(null);
  };

  const handleDialog = async (dialogType: DialogType, id?: string) => {
    if (dialogType === DialogType.Edit && id) {
      try {
        const attendanceResponse = await axiosInstance.get(
          `/v1/attendances/${id}/`
        );
        const attendanceData = attendanceResponse.data;
        setCurrentAttendanceId(id);

        openDialog(
          t('edit.title'),
          '',
          editAttendanceFields(t, attendanceData),
          DialogType.Edit
        );
      } catch (error) {
        console.error('Failed to fetch attendances data', error);
      }
    }
  };

  const handleEditAttendance = async (
    e: FormEvent<Element>,
    updatedData: NewDataField & { id: string }
  ) => {
    e.preventDefault();

    const combineDateTimeToUTC = (date: any, time: any): string => {
      const localDateTime = new Date(`${date}T${time}:00`);
      const year = localDateTime.getUTCFullYear();
      const month = String(localDateTime.getUTCMonth() + 1).padStart(2, '0');
      const day = String(localDateTime.getUTCDate()).padStart(2, '0');
      const hours = String(localDateTime.getUTCHours()).padStart(2, '0');
      const minutes = String(localDateTime.getUTCMinutes()).padStart(2, '0');
      const seconds = String(localDateTime.getUTCSeconds()).padStart(2, '0');

      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000Z`;
    };

    const checkInDateTime = combineDateTimeToUTC(
      updatedData.check_in_date,
      updatedData.check_in_time
    );
    const checkOutDateTime = combineDateTimeToUTC(
      updatedData.check_out_date,
      updatedData.check_out_time
    );

    const formattedUpdatedData = {
      check_in: checkInDateTime,
      check_out: checkOutDateTime,
    };

    try {
      if (updatedData) {
        const response = await axiosInstance.patch(
          `/v1/attendances/${updatedData.id}/`,
          formattedUpdatedData
        );

        setAttendanceData((prevData) =>
          prevData.map((attendance) =>
            attendance.id === updatedData.id ? response.data : attendance
          )
        );
      }

      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Failed to update attendance:', error);
    }
  };
console.log("Attendance Data:",attendanceData);
  return (
    <div>
      <div className="flex flex-row justify-between">
        <div className="flex w-2/5 justify-start">
          <Search
            placeholder={t('table.search')}
            onSearchChange={handleSearchChange}
          />
        </div>
        <div className="flex flex-row space-x-4 items-center">
          <SortBy
            options={attendanceSortingOptions(t)}
            defaultValue=""
            onChange={handleSortChange}
          />
          <EditDialog
            isOpen={isEditDialogOpen}
            onClose={closeDialog}
            handleSubmit={(e: any, updatedData: any) =>
              handleEditAttendance(e, {
                ...updatedData,
                id: currentAttendanceId ?? '',
              })
            }
            title={dialogProps.title}
            subtitle={dialogProps.subtitle ?? ''}
            confirmLabel={t('edit.save')}
            cancelLabel={t('edit.cancel')}
            fields={dialogProps.fields}
            attendanceId={currentAttendanceId ?? ''}
          />
          <DateFilter defaultDate={dateValue} onDateChange={handleDateChange} />
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              className={`form-checkbox h-5 w-5 border-2 ${
                overtimeValue
                  ? 'checked:bg-primary/300 text-primary-300'
                  : 'border-neutral-50 text-neutral-50'
              }`}
              checked={overtimeValue || undefined}
              onChange={() =>
                setOvertimeValue(overtimeValue === null ? false : null)
              }
            />
            <span className="ml-2 text-neutral/300 text-body-2">
              {t('table.checkbox')}
            </span>
          </label>
        </div>
      </div>

      <AttendanceTable
        attendances={attendanceData}
        onEdit={handleDialog.bind(null, DialogType.Edit)}
      />

      <div>
        <Pagination
          allRows={count}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};

export default AttendanceManagement;